import server from '../api/api-services'
import { baseUrl } from '../api/base-url'

// Get all holidays
export const getAllHolidays = () => {
  return server({
    url: baseUrl + 'Holiday/GetAllHoliday',
    method: 'GET'
  })
}

// Get holiday by ID
export const getHolidayById = (id: number) => {
  return server({
    url: baseUrl + `Holiday/GetHolidayById?id=${id}`,
    method: 'GET'
  })
}

// Add holiday
export const addHoliday = (data: any) => {
  return server({
    url: baseUrl + 'Holiday/AddHoliday',
    method: 'POST',
    data: data
  })
}

// Update holiday
export const updateHoliday = (data: any) => {
  return server({
    url: baseUrl + 'Holiday/UpdateHoliday',
    method: 'PUT',
    data: data
  })
}

// Delete holiday
export const deleteHoliday = (id: number) => {
  return server({
    url: baseUrl + `Holiday/DeleteHoliday?id=${id}`,
    method: 'DELETE'
  })
}

// Cache for holidays to avoid repeated API calls
let holidayCache: Date[] | null = null
let cacheExpiry: number | null = null

// Get holidays as Date array (with caching)
export const getHolidaysAsDates = async (forceRefresh = false): Promise<Date[]> => {
  // Check if cache is still valid (5 minutes)
  if (!forceRefresh && holidayCache && cacheExpiry && Date.now() < cacheExpiry) {
    return holidayCache
  }

  try {
    const response = await getAllHolidays()
    if (response.data && response.data.data) {
      // Convert holiday dates to Date objects (set to midnight to avoid timezone issues)
      holidayCache = response.data.data.map((h: any) => {
        const date = new Date(h.holidayDate)
        // Set to midnight local time
        date.setHours(0, 0, 0, 0)
        return date
      })
      // Cache for 5 minutes
      cacheExpiry = Date.now() + 5 * 60 * 1000
      return holidayCache ?? []
    }
  } catch (error) {
    console.warn('Failed to fetch holidays, proceeding without holiday consideration', error)
  }

  return []
}

// Check if a date is a holiday
export const isHoliday = async (date: Date): Promise<boolean> => {
  const holidays = await getHolidaysAsDates()
  const checkDate = new Date(date)
  checkDate.setHours(0, 0, 0, 0)
  return holidays.some(holiday =>
    holiday.getTime() === checkDate.getTime()
  )
}

// Check if a date is a weekend or holiday
export const isNonWorkingDay = async (date: Date): Promise<boolean> => {
  const day = date.getDay()
  // 0 = Sunday, 6 = Saturday
  if (day === 0 || day === 6) {
    return true
  }
  return await isHoliday(date)
}

// Add workdays considering weekends and holidays
export const addWorkdaysWithHolidays = async (startDate: Date, workdays: number): Promise<Date> => {
  let currentDate = new Date(startDate)
  let count = 0

  while (count < workdays) {
    currentDate.setDate(currentDate.getDate() + 1)
    const isNonWorking = await isNonWorkingDay(currentDate)
    if (!isNonWorking) {
      count++
    }
  }

  return currentDate
}

// Calculate workdays between two dates (excluding weekends and holidays)
export const calculateWorkdays = async (startDate: Date, endDate: Date): Promise<number> => {
  let count = 0
  let currentDate = new Date(startDate)
  const end = new Date(endDate)

  while (currentDate <= end) {
    const isNonWorking = await isNonWorkingDay(currentDate)
    if (!isNonWorking) {
      count++
    }
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return count
}
